import Constants from 'expo-constants';

export interface ScreenViewParameters {
  /**
   * Screen name the user is currently viewing.
   */
  screen_name?: string;
  /**
   * Current class associated with the view the user is currently viewing.
   */
  screen_class?: string;
  /**
   * Custom event parameters.
   */
  [key: string]: any;
}

declare global {
  interface Window {
    dataLayer?: any[];
  }
}

const GTM_SCRIPT_ID = 'google-tag-manager';
/** Container ids look like `GTM-XXXXXXX`. */
const GTM_CONTAINER_ID_PATTERN = /^GTM-[A-Z0-9]+$/;

let containerRequested = false;
let warnedAboutContainerId = false;

function getContainerId(): string | undefined {
  const containerId = Constants.expoConfig?.extra?.googleTagManagerContainerId;

  return typeof containerId === 'string' &&
    GTM_CONTAINER_ID_PATTERN.test(containerId)
    ? containerId
    : undefined;
}

/**
 * Load the Google Tag Manager container.
 *
 * The container owns the GA4 destinations and decides which one fires, so the
 * app never references a measurement id directly.
 *
 * @returns whether `window.dataLayer` is available to push to.
 */
function initializeTagManager(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false;
  }

  const containerId = getContainerId();
  if (!containerId) {
    // A rejected id disables analytics with no other symptom, which has already
    // cost two deploys. Say so once rather than failing silently.
    if (!warnedAboutContainerId) {
      warnedAboutContainerId = true;
      console.warn(
        '[analytics] Google Tag Manager is disabled: expected a container id ' +
          `matching ${GTM_CONTAINER_ID_PATTERN} in ` +
          'expoConfig.extra.googleTagManagerContainerId, got ' +
          `${JSON.stringify(
            Constants.expoConfig?.extra?.googleTagManagerContainerId
          )}.`
      );
    }
    return false;
  }

  // dataLayer has to exist before the container script runs so that pushes made
  // during startup are still visible to the tags GTM loads.
  window.dataLayer = window.dataLayer || [];

  if (!containerRequested && !document.getElementById(GTM_SCRIPT_ID)) {
    containerRequested = true;

    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });

    const script = document.createElement('script');
    script.id = GTM_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(
      containerId
    )}`;
    document.head.appendChild(script);
  }

  return true;
}

export async function logAnalyticsScreenView(params: ScreenViewParameters) {
  if (!initializeTagManager()) return;

  // Do not include the query string: VPC links can contain an SSO ticket or
  // appointment id, neither of which belongs in analytics.
  const pagePath = window.location.pathname;
  window.dataLayer?.push({
    event: 'screen_view',
    ...params,
    page_location: `${window.location.origin}${pagePath}`,
    page_path: pagePath,
    page_title: document.title
  });
}

export async function logAnalyticsEvent(
  eventName: string,
  params?: { [key: string]: any }
) {
  if (!initializeTagManager()) return;

  window.dataLayer?.push({ event: eventName, ...params });
}

export async function resetAnalyticsData() {
  // For mobile app implementation see `analytics.native.ts`
}

export async function setCrashlyticsUserId(id: string) {
  // For mobile app implementation see `analytics.native.ts`
  void id;
}

/**
 * [QHDEV-LOGGING] No-ops on web; see `analytics.native.ts` for the Crashlytics
 * implementation. Kept here so `src/common/logger.ts` has one import path.
 */
export function logCrashlyticsMessage(message: string): void {
  void message;
}

export function recordCrashlyticsError(error: Error): void {
  void error;
}
